import { loadTile, type TileKey } from '../siteMapTileLoader'

/**
 * One generation's imagery session (009, research R6): a shared tile cache so
 * the overview and overlapping sheets fetch each tile once, and a FIFO
 * concurrency limit so a 400-tile map book never fires every request at once.
 * A failed tile resolves to null and stays cached — no retry within a session.
 */

export interface TileSession {
  load(url: string, key?: TileKey): Promise<HTMLImageElement | null>
  stats: { distinct: number; maxInFlight: number }
}

export function createTileSession({ concurrency = 6 }: { concurrency?: number } = {}): TileSession {
  const cache = new Map<string, Promise<HTMLImageElement | null>>()
  const queue: Array<() => void> = []
  const stats = { distinct: 0, maxInFlight: 0 }
  let inFlight = 0

  const release = () => {
    inFlight--
    queue.shift()?.()
  }

  const acquire = (): Promise<void> =>
    new Promise((resolve) => {
      const start = () => {
        inFlight++
        stats.maxInFlight = Math.max(stats.maxInFlight, inFlight)
        resolve()
      }
      if (inFlight < concurrency) start()
      else queue.push(start)
    })

  return {
    stats,
    load(url, key) {
      const hit = cache.get(url)
      if (hit) return hit
      stats.distinct++
      const pending = acquire().then(() =>
        (key ? loadTile(url, key) : loadTile(url)).then(
          (img) => {
            release()
            return img
          },
          () => {
            release()
            return null
          }
        )
      )
      cache.set(url, pending)
      return pending
    },
  }
}

/** Distinct tile URLs one map book may fetch: the SW imagery caches hold 500
    entries per origin, so a book can never evict the whole interactive cache */
export const MAP_BOOK_TILE_BUDGET = 400

/** A canvas frame in world px at its (fractional) zoom */
export interface TileFrame {
  zoom: number
  originX: number
  originY: number
  width: number
  height: number
}

/** Tile cells a frame needs when its imagery comes from `tileZoom` */
export function tileCells(frame: TileFrame, tileZoom: number): number {
  const size = 256 * 2 ** (frame.zoom - tileZoom)
  const cols =
    Math.floor((frame.originX + frame.width) / size) - Math.floor(frame.originX / size) + 1
  const rows =
    Math.floor((frame.originY + frame.height) / size) - Math.floor(frame.originY / size) + 1
  return cols * rows
}

/**
 * Imagery zoom per frame (R6), decided before any fetch so imagery never stops
 * halfway down a page: start at floor(zoom), then while the total exceeds the
 * budget, step down the frame needing the most cells (ties: earliest frame —
 * callers pass sheets in number order, then the overview). Each step quarters
 * that frame's cells; badges are unaffected. Never below `minZoom`.
 */
export function planTileZooms(
  frames: TileFrame[],
  limit = MAP_BOOK_TILE_BUDGET,
  minZoom = 0
): number[] {
  const zooms = frames.map((f) => Math.max(minZoom, Math.floor(f.zoom)))
  const counts = frames.map((f, i) => tileCells(f, zooms[i] ?? minZoom))
  let total = counts.reduce((a, b) => a + b, 0)
  while (total > limit) {
    let pick = -1
    counts.forEach((c, i) => {
      if ((zooms[i] ?? minZoom) > minZoom && (pick < 0 || c > (counts[pick] ?? 0))) pick = i
    })
    const frame = frames[pick]
    if (!frame) break // everything is at the floor
    const z = (zooms[pick] ?? minZoom) - 1
    zooms[pick] = z
    const next = tileCells(frame, z)
    total += next - (counts[pick] ?? 0)
    counts[pick] = next
  }
  return zooms
}
